import { useEffect, useRef } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Underline,
} from "lucide-react";
import InsertPlaceholderMenu from "@/components/placeholder/InsertPlaceholderMenu";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SP } from "@/constants/testIds";
import { placeholderPickerGroups, placeholderToken } from "@/lib/placeholderVocabulary";
import { cn } from "@/lib/utils";

const SMART_PHRASE_PLACEHOLDER_GROUPS = placeholderPickerGroups();

function ToolbarButton({ title, onClick, children }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={title}
      className="h-8 w-8 text-slate-600"
      onMouseDown={(event) => {
        event.preventDefault();
        onClick?.();
      }}
    >
      {children}
    </Button>
  );
}

function selectionIsInside(el, node) {
  if (!el || !node) return false;
  const target = node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
  return Boolean(target && el.contains(target));
}

export default function SmartPhraseContentEditor({
  format = "richtext",
  value = "",
  onChange,
  className,
}) {
  const editorRef = useRef(null);
  const lastHtml = useRef("");
  const savedRange = useRef(null);

  useEffect(() => {
    if (format !== "richtext") return;
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastHtml.current && el.innerHTML !== value) {
      el.innerHTML = value || "";
      lastHtml.current = value || "";
    }
  }, [format, value]);

  const emitHtml = () => {
    const html = editorRef.current?.innerHTML || "";
    lastHtml.current = html;
    onChange?.(html);
  };

  const saveSelection = () => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    if (!selectionIsInside(el, sel.anchorNode)) return;
    savedRange.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const range = savedRange.current;
    if (!range) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const run = (command, arg) => {
    restoreSelection();
    document.execCommand(command, false, arg);
    saveSelection();
    emitHtml();
  };

  const insertHtml = (html) => {
    restoreSelection();
    const ok = document.execCommand("insertHTML", false, html);
    if (!ok) {
      const el = editorRef.current;
      if (el) {
        el.innerHTML = `${el.innerHTML}${html}`;
      }
    }
    saveSelection();
    emitHtml();
  };

  const insertPlaceholder = (token) => {
    insertHtml(`${placeholderToken(token)}\u00a0`);
  };

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    run("createLink", url);
  };

  if (format === "plaintext") {
    return (
      <Textarea
        data-testid={SP.contentEditor}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        placeholder="Enter SmartPhrase content here..."
        className={cn("min-h-[320px] resize-y rounded-lg bg-white font-mono text-sm", className)}
      />
    );
  }

  return (
    <div className={cn("overflow-hidden rounded-lg border border-slate-200 bg-white", className)}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1 py-1">
        <ToolbarButton title="Bold" onClick={() => run("bold")}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Italic" onClick={() => run("italic")}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Underline" onClick={() => run("underline")}>
          <Underline className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton title="Bulleted list" onClick={() => run("insertUnorderedList")}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => run("insertOrderedList")}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton title="Align left" onClick={() => run("justifyLeft")}>
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Align center" onClick={() => run("justifyCenter")}>
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Align right" onClick={() => run("justifyRight")}>
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <InsertPlaceholderMenu
          groups={SMART_PHRASE_PLACEHOLDER_GROUPS}
          testId={SP.insertPlaceholder}
          itemTestIdPrefix={SP.contentEditor}
          searchTestId={SP.placeholderSearch}
          searchable
          onSelect={insertPlaceholder}
        />
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton title="Insert link" onClick={insertLink}>
          <Link2 className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <div
        ref={editorRef}
        data-testid={SP.contentEditor}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="SmartPhrase content"
        className="min-h-[320px] max-h-[520px] overflow-y-auto px-4 py-3 text-sm leading-relaxed text-slate-900 outline-none [&:empty]:before:pointer-events-none [&:empty]:before:text-slate-400 [&:empty]:before:content-[attr(data-placeholder)]"
        data-placeholder="Enter SmartPhrase content here..."
        onInput={() => {
          saveSelection();
          emitHtml();
        }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onBlur={() => {
          saveSelection();
          emitHtml();
        }}
      />
    </div>
  );
}
